import { useExperiments, useEnvironment } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import { getIsPostPageBundle } from '@wix/communities-blog-client-common';
import { showMenuLoginButton as legacyShowMenuLoginButton } from '@app/components/Post/legacyStylesParams';
import stylesParams from '@app/components/Post/stylesParams';
import { OOI_EXPERIMENTS } from '@app/experiments';
import { useSelector } from '../components/runtime-context';
import {
  getIsBlogMenuEnabled,
  getIsBlogMenuCategoryLabelsEnabled,
  getIsBlogMenuSearchEnabled,
  getIsBlogMenuLoginButtonEnabled,
} from '../selectors/app-settings-selectors';
import { getIsMemberAreaInstalled } from '../store/communities-context/communities-context-selectors';

type BlogMenuSettings = {
  showBlogMenu: boolean;
  showCategoryLabels: boolean;
  showSearch: boolean;
  showLoginButton: boolean;
};

const useBlogMenuSettings = (): BlogMenuSettings => {
  const { experiments } = useExperiments();
  const styles = useStyles();
  const { isEditor, isEditorX: isStudio } = useEnvironment();

  const isPostPage = useSelector(getIsPostPageBundle);
  const isMembersAreaInstalled = useSelector(getIsMemberAreaInstalled);
  const isInNewPostPage =
    isPostPage && experiments.enabled(OOI_EXPERIMENTS.USE_NEW_POST_PAGE_SETTINGS_URL);

  const showBlogMenuFromStyle = isStudio
    ? styles.get(stylesParams.showMenuCategoryLabels)
    : styles.get(stylesParams.showMenu);
  const showBlogMenuFromStore = useSelector(getIsBlogMenuEnabled);
  const showBlogMenu = isInNewPostPage ? showBlogMenuFromStyle : showBlogMenuFromStore;

  const showCategoryLabelsFromStyle = styles.get(stylesParams.showMenuCategoryLabels);
  const showCategoryLabelsFromStore = useSelector(getIsBlogMenuCategoryLabelsEnabled);
  const showCategoryLabels = isInNewPostPage
    ? showCategoryLabelsFromStyle
    : showCategoryLabelsFromStore;

  const showSearchFromStyle = styles.get(stylesParams.showMenuSearch);
  const showSearchFromStore = useSelector(getIsBlogMenuSearchEnabled);
  const showSearch = isInNewPostPage ? showSearchFromStyle : showSearchFromStore;

  const getLoginValueForUndefinedStyle = () => {
    if (isEditor) {
      return undefined; // use style param value
    }

    const isUndefined =
      styles
        .getStylesForAllBreakpoints()
        .some((bp) =>
          legacyShowMenuLoginButton.key ? legacyShowMenuLoginButton.key in bp.booleans : false,
        ) === false;

    return isUndefined ? !isMembersAreaInstalled : undefined;
  };

  const showLoginButtonFromStyle =
    getLoginValueForUndefinedStyle() ?? styles.get(stylesParams.showMenuLoginButton);
  const showLoginButtonFromStore = useSelector(getIsBlogMenuLoginButtonEnabled);
  const showLoginButton = isInNewPostPage ? showLoginButtonFromStyle : showLoginButtonFromStore;

  return {
    showBlogMenu,
    showCategoryLabels,
    showSearch,
    showLoginButton,
  };
};

export default useBlogMenuSettings;
